import { useFlag } from '@calendly/feature-check-react'
import { isEmpty } from 'lodash-es'
import moment from 'moment-timezone'
import { useContext, useEffect, useState } from 'react'

import {
  OriginalEventBookedStep,
  BookedStep,
  CancelledStep,
  FromRescheduledStep,
  RescheduledStep,
  StartsStep,
  WorkflowStep,
  ReconfirmationStep,
} from './steps'
import { MeetingDetailDependenciesContext } from '../../../meeting_detail_context'

export const useEventTimelineSteps = (selectedEvent) => {
  const dependencies = useContext(MeetingDetailDependenciesContext)
  if (!dependencies?.useGetTimelineDataQuery) {
    throw new Error('useEventTimelineSteps must be wrapped in a MeetingDetailDependenciesProvider with useGetTimelineDataQuery')
  }

  const [steps, setSteps] = useState([])
  const workflowsExtractionEnabled = useFlag('raven_workflows_extraction')
  // Workflows API requires the internal id, not uuid - skip if id is not available
  const eventId = selectedEvent?.id
  const { data } = dependencies.useGetTimelineDataQuery(
    { eventId, workflowsExtractionEnabled },
    { skip: !eventId }
  )

  useEffect(() => {
    const newSteps = []

    if (selectedEvent.oldSchedulable) {
      newSteps.push(
        {
          occurredAt: selectedEvent.oldSchedulable.createdAt,
          step: <OriginalEventBookedStep key="booked" selectedEvent={selectedEvent} />
        },
        {
          occurredAt: selectedEvent.oldSchedulable.cancelledAt,
          step: <FromRescheduledStep key="from-rescheduled" selectedEvent={selectedEvent} />
        }
      )
    } else {
      newSteps.push(
        {
          occurredAt: selectedEvent.scheduledAt,
          step: <BookedStep key="booked" selectedEvent={selectedEvent} />
        }
      )
    }

    // Supergraph list/detail nodes expose `cancelled` but often omit `cancellation`
    // until scheduled-event enrichment lands (or never for non-Calendly). Require
    // cancelledAt before rendering Cancelled/Rescheduled steps so the rail does not crash.
    const cancelledAt = selectedEvent.cancellation?.cancelledAt

    if (selectedEvent.newSchedulable && cancelledAt) {
      newSteps.push(
        {
          occurredAt: selectedEvent.newSchedulable.createdAt,
          step: <RescheduledStep key="rescheduled" selectedEvent={selectedEvent} />
        }
      )
    } else if (selectedEvent.cancelled && cancelledAt) {
      newSteps.push(
        {
          occurredAt: cancelledAt,
          step: <CancelledStep key="cancelled" selectedEvent={selectedEvent} />
        }
      )
    } else if (!selectedEvent.cancelled && !selectedEvent.newSchedulable) {
      newSteps.push(
        {
          occurredAt: selectedEvent.startTime,
          step: <StartsStep key="starts" selectedEvent={selectedEvent} />
        }
      )
    }

    if (!isEmpty(data)) {
      data.forEach((entry) => {
        if (entry.type === 'invitee_confirmed') {
          newSteps.push(
            {
              occurredAt: entry.timestamp,
              step: <ReconfirmationStep key={entry.timestamp} entry={entry} />
            }
          )
        }
      })

      newSteps.push(
        ...data
          .filter(({ type }) => !['event_booked', 'event_started', 'event_canceled', 'invitee_confirmed'].includes(type))
          .map((workflow) => (
            {
              occurredAt: workflow.timestamp,
              step: <WorkflowStep key={workflow.timestamp} workflow={workflow} />
            }
          ))
      )
    }

    newSteps.sort((a, b) => moment(a.occurredAt).diff(moment(b.occurredAt)))

    setSteps(newSteps)
  }, [selectedEvent, data])

  return { steps }
}
